/**
 * useAvailGrid — operator 14-day availability grid (B.10 «График»).
 *
 * State shape: Grid = { [dateKey]: { [hour]: "free"|"busy"|"none" } } for the
 * 17 display hours (10:00 → 02:00). Adapted from v3 useAvailGrid with A17
 * zones: the nearest `LOCKED_DAYS` days are закреплено (read-only), the rest
 * editable. Loads/saves through /api/operator-availability (free/busy stored,
 * none deleted). Pointer painting lives in the component; this hook owns state
 * + IO + counters + bulk ops.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { todayBusiness, addDays, fmtDateApi } from "../utils/date";

export type CellStatus = "free" | "busy" | "none";

/** Display hours: 10..23 day, then 00/01/02 night. 17 columns. */
export const AVAIL_HOURS = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2];

const LOCKED_DAYS = 7;
const TOTAL_DAYS = 14;
/* ВРЕМЕННО (26.08, владелец — переход на новый юнибукинг): до этого момента
   операторам открыты ВСЕ дни графика (обычно ближайшие 7 закреплены). */
export const AVAIL_UNLOCK_UNTIL = Date.parse("2026-08-26T20:00:00Z");
/* После этого момента незаполненные (серые) клетки считаются СВОБОДНЫМИ. */
export const AVAIL_DEFAULT_FREE_AFTER = Date.parse("2026-08-26T12:00:00Z");
const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"] as const;

export interface AvailDay {
  key: string; // fmtDateApi "2026-06-01"
  dow: number; // 0..6 (Sunday=0)
  dowLabel: string;
  dayNum: number;
  today: boolean;
  locked: boolean; // A17 — nearest LOCKED_DAYS закреплено
  calWeekend: boolean; // сб/вс — only label styling
  weekend: boolean; // выходной (EO/OO-set) — non-interactive
}

type Grid = Record<string, Record<number, CellStatus>>;

function buildDays(weekendKeys: Set<string>, unlocked = false): AvailDay[] {
  const base = todayBusiness();
  const out: AvailDay[] = [];
  for (let i = 0; i < TOTAL_DAYS; i++) {
    const d = addDays(base, i);
    const key = fmtDateApi(d);
    const dow = d.getUTCDay();
    out.push({
      key,
      dow,
      dowLabel: WD[dow] ?? "",
      dayNum: d.getUTCDate(),
      today: i === 0,
      /* 28.08 (владелец): ОО может разово открыть оператору закреплённые дни */
      locked: i < LOCKED_DAYS && Date.now() > AVAIL_UNLOCK_UNTIL && !unlocked,
      calWeekend: dow === 0 || dow === 6,
      weekend: weekendKeys.has(key),
    });
  }
  return out;
}

function emptyGrid(days: AvailDay[]): Grid {
  const g: Grid = {};
  for (const d of days) {
    const row: Record<number, CellStatus> = {};
    for (const h of AVAIL_HOURS) row[h] = "none";
    g[d.key] = row;
  }
  return g;
}

export interface AvailGridApi {
  days: AvailDay[];
  grid: Grid;
  counters: { free: number; busy: number; none: number };
  loading: boolean;
  error: string | null;
  dirty: boolean;
  saving: boolean;
  sent: boolean;
  setCell: (key: string, hour: number, status: CellStatus) => void;
  cycleCell: (key: string, hour: number) => void;
  fillEditable: (status: CellStatus) => void;
  save: () => void;
  /** Окно правки закреплённых дней от офиса (ISO) или null. */
  unlockUntil: string | null;
}

const CYCLE: Record<CellStatus, CellStatus> = { none: "free", free: "busy", busy: "none" };

export function useAvailGrid(operatorId: string | undefined, weekendKeys?: Set<string>): AvailGridApi {
  /* окно правки закреплённых дней, выданное офисом (28.08, владелец) */
  const [unlockUntil, setUnlockUntil] = useState<string | null>(null);
  const days = useMemo(() => buildDays(weekendKeys ?? new Set(), !!unlockUntil), [weekendKeys, unlockUntil]);
  const [grid, setGrid] = useState<Grid>(() => emptyGrid(days));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const sentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* load saved grid */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    if (!operatorId) {
      setLoading(false);
      return;
    }
    const qs = new URLSearchParams({ operator_id: operatorId });
    api<{ grid: Record<string, Record<string, string>>; unlock_until?: string | null }>(`/api/operator-availability?${qs}`)
      .then((res) => {
        if (!alive) return;
        setUnlockUntil(res.unlock_until ?? null);
        const fresh = emptyGrid(days);
        for (const [date, hours] of Object.entries(res.grid ?? {})) {
          if (!fresh[date]) continue;
          for (const [h, st] of Object.entries(hours)) {
            const hour = Number(h);
            if (st === "free" || st === "busy") fresh[date][hour] = st;
          }
        }
        /* дедлайн прошёл — незаполненное зелёное (сохранится в БД при следующем «Отправить») */
        if (Date.now() > AVAIL_DEFAULT_FREE_AFTER) for (const d of days) for (const h of AVAIL_HOURS) { if (fresh[d.key] && fresh[d.key]![h] === "none") fresh[d.key]![h] = "free"; }
        setGrid(fresh);
        setDirty(false);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить график");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [operatorId, days]);

  const counters = useMemo(() => {
    let free = 0, busy = 0, none = 0;
    for (const d of days) {
      const row = grid[d.key];
      if (!row) continue;
      for (const h of AVAIL_HOURS) {
        const s = row[h];
        if (s === "free") free++;
        else if (s === "busy") busy++;
        else none++;
      }
    }
    return { free, busy, none };
  }, [grid, days]);

  const markDirty = useCallback(() => {
    setDirty(true);
    setSent(false);
  }, []);

  const setCell = useCallback((key: string, hour: number, status: CellStatus) => {
    setGrid((g) => {
      const row = g[key];
      if (!row || row[hour] === status) return g;
      return { ...g, [key]: { ...row, [hour]: status } };
    });
    markDirty();
  }, [markDirty]);

  const cycleCell = useCallback((key: string, hour: number) => {
    setGrid((g) => {
      const row = g[key];
      if (!row) return g;
      return { ...g, [key]: { ...row, [hour]: CYCLE[row[hour] ?? "none"] } };
    });
    markDirty();
  }, [markDirty]);

  const fillEditable = useCallback((status: CellStatus) => {
    setGrid((g) => {
      const next: Grid = { ...g };
      for (const d of days) {
        if (d.locked || d.weekend) continue;
        const row: Record<number, CellStatus> = { ...next[d.key] };
        for (const h of AVAIL_HOURS) row[h] = status;
        next[d.key] = row;
      }
      return next;
    });
    markDirty();
  }, [days, markDirty]);

  const save = useCallback(() => {
    if (!operatorId || saving) return;
    setSaving(true);
    api("/api/operator-availability", { method: "PUT", body: { operator_id: operatorId, grid } })
      .then(() => {
        setDirty(false);
        setSaving(false);
        setSent(true);
        if (sentTimer.current) clearTimeout(sentTimer.current);
        sentTimer.current = setTimeout(() => setSent(false), 2500);
      })
      .catch((e: unknown) => {
        setSaving(false);
        setError(e instanceof Error ? e.message : "Не удалось сохранить график");
      });
  }, [operatorId, grid, saving]);

  useEffect(() => () => {
    if (sentTimer.current) clearTimeout(sentTimer.current);
  }, []);

  return { days, grid, counters, loading, error, dirty, saving, sent, setCell, cycleCell, fillEditable, save, unlockUntil };
}
